import React, { useEffect, useRef, useState } from "react";
import { docsApi, entitiesApi } from "@/lib/api";
import { PageShell } from "./Overview";
import { Upload, FileText, Trash2, Download, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { id: "incorporation", label: "Incorporation certificate" },
  { id: "moa", label: "MOA / AOA" },
  { id: "shareholder", label: "Shareholder agreement" },
  { id: "tax", label: "Tax filing" },
  { id: "license", label: "License" },
  { id: "other", label: "Other" },
];

const Documents = () => {
  const [docs, setDocs] = useState([]);
  const [entities, setEntities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [docType, setDocType] = useState("other");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState("");
  const [genEntityId, setGenEntityId] = useState("");
  const fileRef = useRef(null);

  const load = () => docsApi.list().then(setDocs).catch(() => {});
  useEffect(() => {
    load();
    entitiesApi.list().then((es) => {
      setEntities(es);
      if (es.length) setGenEntityId(es[0].id);
    }).catch(() => {});
    docsApi.templates().then(setTemplates).catch(() => {});
  }, []);

  const onFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      await docsApi.upload(f, entityId || null, docType);
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (d) => {
    const res = await docsApi.download(d.id);
    const byte = atob(res.content_b64);
    const arr = new Uint8Array(byte.length);
    for (let i = 0; i < byte.length; i++) arr[i] = byte.charCodeAt(i);
    const blob = new Blob([arr], { type: res.mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = res.name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const remove = async (d) => {
    await docsApi.remove(d.id);
    toast.success("Deleted");
    load();
  };

  const generate = async (templateId) => {
    if (!genEntityId) { toast.error("Pick an entity first"); return; }
    setGenerating(templateId);
    try {
      const doc = await docsApi.generate(genEntityId, templateId);
      toast.success(`${doc.name} generated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating("");
    }
  };

  return (
    <PageShell title="Document vault" subtitle={`${docs.length} files`}>
      {templates.length > 0 && (
        <div className="border border-border-subtle bg-surface-card p-6 mb-6" data-testid="generate-panel">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-artha-amber" />
                <span className="text-xs font-mono-accent uppercase tracking-[0.22em] text-artha-amber">Generate from template</span>
              </div>
              <h2 className="h-display text-2xl">Draft legal documents in one click.</h2>
              <p className="text-sm text-ink-muted mt-2 max-w-xl">
                Artha pre-fills MOA / AOA / Shareholder Agreement drafts with your entity data. Review, tweak, and route to your assigned partner for execution.
              </p>
            </div>
            <div className="min-w-[220px]">
              <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted mb-2">Entity</label>
              <select value={genEntityId} onChange={(e) => setGenEntityId(e.target.value)} data-testid="generate-entity-select"
                className="w-full bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber">
                {entities.length === 0 && <option value="">— no entities yet —</option>}
                {entities.map((e) => <option key={e.id} value={e.id}>{e.company_name} · {e.country_name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border-subtle border border-border-subtle">
            {templates.map((t) => (
              <button key={t.id} onClick={() => generate(t.id)} disabled={!genEntityId || generating === t.id}
                data-testid={`generate-${t.id}`}
                className="bg-surface-app p-5 text-left hover:bg-surface-card transition-colors disabled:opacity-50">
                <Wand2 size={16} className="text-artha-amber mb-3" strokeWidth={1.5} />
                <div className="h-display text-lg">{t.title}</div>
                <div className="mt-2 text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted">
                  {generating === t.id ? "Drafting…" : "Generate PDF"}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border-subtle bg-surface-card p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div className="md:col-span-1">
          <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted mb-2">Link to entity</label>
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} data-testid="doc-entity-select"
            className="w-full bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber">
            <option value="">— unassigned —</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted mb-2">Type</label>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type-select"
            className="w-full bg-transparent border border-border-subtle px-3 py-2.5 text-sm focus:outline-none focus:border-artha-amber">
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <input ref={fileRef} type="file" onChange={(e) => onFile(e.target.files?.[0])} className="hidden"
            data-testid="doc-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="doc-upload-button"
            className="w-full bg-artha-amber text-artha-ink py-3 text-sm tracking-tight inline-flex items-center justify-center gap-2 hover:bg-artha-cream transition-colors disabled:opacity-60">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload file (max 8MB)"}
          </button>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="border border-dashed border-border-subtle p-16 text-center">
          <FileText size={36} className="mx-auto text-ink-muted mb-4" strokeWidth={1.25} />
          <div className="h-display text-2xl italic text-ink-muted">Nothing yet.</div>
          <p className="mt-2 text-sm text-ink-muted">Upload a file or generate a draft from a template above.</p>
        </div>
      ) : (
        <div className="border border-border-subtle divide-y divide-border-subtle">
          {docs.map((d) => (
            <div key={d.id} className="grid grid-cols-12 gap-4 items-center px-5 py-4" data-testid={`doc-row-${d.id}`}>
              <div className="col-span-6 flex items-center gap-3">
                <FileText size={18} className={d.generated ? "text-artha-amber" : "text-ink-muted"} strokeWidth={1.25} />
                <div>
                  <div className="text-sm">{d.name}</div>
                  {d.generated && (
                    <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-artha-amber mt-0.5">
                      Artha-generated draft
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2 text-xs font-mono-accent uppercase tracking-[0.18em] text-ink-muted">{d.type}</div>
              <div className="col-span-2 text-xs font-mono-accent text-ink-muted">{(d.size_bytes / 1024).toFixed(1)} KB</div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => download(d)} data-testid={`doc-download-${d.id}`}
                  className="p-2 border border-border-subtle hover:border-artha-amber hover:text-artha-amber transition-colors"><Download size={14} /></button>
                <button onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`}
                  className="p-2 border border-border-subtle hover:border-[#C94F4F] hover:text-[#C94F4F] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default Documents;
